import { STAGE } from '../../design/lab/palette';
import React, { useRef, useEffect, useState } from 'react';
import { FlintInput } from '../../agent/charts';

export interface FlintChartProps {
    input: FlintInput;
    title?: string;
    caption?: string;
    accent?: string;
}

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

/**
 * FlintChart — renders a Flint semantic chart spec.
 *
 * Flint (microsoft/flint-chart, MIT) is a compiler, not a renderer: it turns a
 * semantic spec — fields, semantic types, chart type — into a native config for
 * a real charting library, deriving scales, axes and spacing itself. Here it
 * compiles to Chart.js.
 *
 * Both libraries are imported dynamically, so a visitor who never asks the
 * agent for a chart never downloads either of them.
 */
const FlintChart: React.FC<FlintChartProps> = ({ input, title, caption, accent = STAGE.cyan }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const [{ assembleChartjs }, chartjs] = await Promise.all([
                    import('flint-chart'),
                    import('chart.js/auto'),
                ]);
                if (cancelled) return;

                const config: any = assembleChartjs(input as any);

                // Theme the compiled config for a dark terminal surface. Flint
                // decides structure; the palette stays ours.
                config.options = config.options ?? {};
                config.options.responsive = true;
                config.options.maintainAspectRatio = false;
                config.options.plugins = {
                    ...(config.options.plugins ?? {}),
                    legend: { display: false },
                };
                const grid = 'rgba(255,255,255,0.07)';
                const tick = { color: STAGE.muted, font: { family: "'JetBrains Mono', monospace", size: 10 } };
                config.options.scales = Object.fromEntries(
                    Object.entries(config.options.scales ?? { x: {}, y: {} }).map(([k, v]) => [
                        k, { ...(v as object), grid: { color: grid }, ticks: tick },
                    ])
                );
                for (const ds of config.data?.datasets ?? []) {
                    ds.backgroundColor = `${accent}bb`;
                    ds.borderColor = accent;
                    ds.borderWidth = 0;
                    ds.borderRadius = 3;
                }

                const canvas = canvasRef.current;
                if (!canvas) return;
                chartRef.current?.destroy?.();
                chartRef.current = new chartjs.Chart(canvas, config);
                setReady(true);
            } catch (err: any) {
                if (!cancelled) setError(err?.message ?? String(err));
            }
        })();

        return () => {
            cancelled = true;
            chartRef.current?.destroy?.();
            chartRef.current = null;
        };
    }, [input, accent]);

    return (
        <div style={{
            margin: '10px 0 12px',
            padding: '14px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
        }}>
            {title && (
                <div style={{ ...mono, fontSize: '11.5px', color: STAGE.ink, fontWeight: 700, marginBottom: '2px' }}>
                    {title}
                </div>
            )}
            {caption && (
                <div style={{ ...mono, fontSize: '10px', color: STAGE.muted, marginBottom: '10px' }}>
                    {caption}
                </div>
            )}

            <div style={{ position: 'relative', height: 240 }}>
                <canvas ref={canvasRef} />
                {!ready && !error && (
                    <div style={{
                        ...mono, fontSize: '11px', color: STAGE.muted,
                        position: 'absolute', inset: 0,
                        display: 'grid', placeItems: 'center',
                    }}>compiling chart…</div>
                )}
            </div>

            {error && (
                <div style={{ ...mono, fontSize: '11px', color: STAGE.red }}>
                    chart failed: {error}
                </div>
            )}
            <div style={{ ...mono, fontSize: '9.5px', color: STAGE.muted, marginTop: '8px' }}>
                compiled by flint-chart → chart.js
            </div>
        </div>
    );
};

export default FlintChart;
